import type { Texture } from 'pixi.js';
import type { DrawingDTO } from '@shared/protocol/messages';
import { BOIL_FRAMES, type AssetCache } from '../game/rendering/AssetCache';
import { inkBounds, renderDrawing } from './strokes';

const TEX_SIZE = 256;

/** Hash a drawing so identical drawings share textures. */
export function drawingKey(d: DrawingDTO): string {
  let h = 2166136261;
  for (const s of d.strokes) {
    const str = `${s.c}${s.s}${s.e ? 1 : 0}`;
    for (let i = 0; i < str.length; i++) h = Math.imul(h ^ str.charCodeAt(i), 16777619);
    for (const v of s.p) h = Math.imul(h ^ v, 16777619);
  }
  return `drawing:${(h >>> 0).toString(36)}:${d.strokes.length}`;
}

/**
 * Turns player drawings into boil-animated textures. Each frame re-renders the
 * strokes with a slightly different jitter, like re-tracing the doodle.
 */
export class DrawingTextures {
  constructor(private readonly assets: AssetCache) {}

  acquire(d: DrawingDTO): { key: string; textures: Texture[] } {
    const key = drawingKey(d);
    const textures = this.assets.acquireDynamic(key, () => {
      const frames: HTMLCanvasElement[] = [];
      for (let f = 0; f < BOIL_FRAMES; f++) {
        const c = document.createElement('canvas');
        c.width = TEX_SIZE;
        c.height = TEX_SIZE;
        const ctx = c.getContext('2d');
        if (ctx) {
          fitInk(ctx, d);
          renderDrawing(ctx, d, TEX_SIZE, { boilSeed: f === 0 ? 0 : 977 * f, boilAmount: 1.4 });
        }
        frames.push(c);
      }
      return frames;
    });
    return { key, textures };
  }

  release(key: string): void {
    this.assets.release(key);
  }
}

/**
 * Zoom so the inked area fills the texture (centred, standing on the bottom
 * edge) — small doodles become full-size characters in battle.
 */
function fitInk(ctx: CanvasRenderingContext2D, d: DrawingDTO): void {
  const b = inkBounds(d);
  if (!b || b.w <= 0 || b.h <= 0) return;
  const unit = TEX_SIZE / d.w;
  const target = TEX_SIZE * 0.94;
  const scale = Math.min(3, target / (b.w * unit), target / (b.h * unit));
  const cx = (b.x + b.w / 2) * unit;
  const bottom = (b.y + b.h) * unit;
  ctx.translate(TEX_SIZE / 2, TEX_SIZE * 0.985);
  ctx.scale(scale, scale);
  ctx.translate(-cx, -bottom);
}

/** Draw a drawing (or nothing) into a DOM canvas thumbnail. */
export function drawThumbnail(canvas: HTMLCanvasElement, d: DrawingDTO): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.save();
  const b = inkBounds(d);
  if (b && b.w > 0 && b.h > 0) {
    const unit = canvas.width / d.w;
    const scale = Math.min(3, (canvas.width * 0.9) / (b.w * unit), (canvas.height * 0.9) / (b.h * unit));
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.scale(scale, scale);
    ctx.translate(-(b.x + b.w / 2) * unit, -(b.y + b.h / 2) * unit);
  }
  renderDrawing(ctx, d, canvas.width);
  ctx.restore();
}
